import { Alert, Box, Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
// import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceGroupAccount } from 'src/shared/models/invoiceGroup/invoiceGroupAccount';
import { createInvoiceGroupAccounts, getInvoiceGroupAccountsById, updateInvoiceGroupAccounts } from 'src/shared/services/invoiceGroupService';
import { getFortnoxAccounts, getIntegrationsByOwnerId } from 'src/shared/services/accountingIntegrationService';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Trans, useTranslation } from 'react-i18next';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { toast } from 'react-toastify';
import { useSchemaInvoiceGroupAccount } from '../schemas/useSchemaInvoiceGroupAccount';
import { AdminInvoiceGroupAccountingIntegrationPanel } from './adminInvoiceGroupFortnoxPanel';
import { FortnoxAccountPicker } from './fortnoxAccountPicker';

export const AdminInvoiceGroupAccountingTab = () => {
  const params = useParams();
  const { t } = useTranslation(['invoice']);
  const [loading, setLoading] = useState(true);
  const isMounted = useIsMounted();
  const [invoiceGroupAccounts, setInvoiceGroupAccounts] = useState<InvoiceGroupAccount>();
  const [hasExistingAccount, setHasExistingAccount] = useState(false);
  const [fortnoxProvider, setFortnoxProvider] = useState<string | null>(null);
  const [fortnoxAccountNumbers, setFortnoxAccountNumbers] = useState<Set<string> | null>(null);
  const ownerId = params.id ? Number(params.id) : 0;

  const invoiceGroupAccountSchema = useSchemaInvoiceGroupAccount();
  type InvoiceGroupAccountInterface = InferType<typeof invoiceGroupAccountSchema>;

  const formMethods = useForm<InvoiceGroupAccountInterface>({
    resolver: yupResolver(invoiceGroupAccountSchema),
    mode: 'all',
    defaultValues: {
      bankAccount: '',
      memberAccount: '',
      depositAccount: '',
      fsPayServiceAccount: '',
      voucherSerie: '',
      eventAccount: '',
      buyAccount: '',
      clearingSwishAccount: '',
      clearingStripeAccount: '',
      excessAccount: '',
    },
  });

  const { handleSubmit, formState, reset, trigger, setValue, watch } = formMethods;
  const { isDirty, isValid } = formState;

  const getFortnoxError = (name: string): string | undefined => {
    if (!fortnoxAccountNumbers) return undefined;
    const value = watch(name as any);
    if (!value || value === '') return undefined;
    if (!fortnoxAccountNumbers.has(value)) {
      return t('invoice:adminInvoiceGroupAccountingTab.accountNotInFortnox') as string;
    }
    return undefined;
  };

  const renderAccountField = (label: string, name: string) => {
    const fortnoxError = getFortnoxError(name);
    return (
      <Stack direction='row' spacing={1} alignItems='flex-start'>
        <Box sx={{ flex: 1 }}>
          <ControlledTextField label={label} name={name} helperText={fortnoxError} error={!!fortnoxError} />
        </Box>
        {fortnoxProvider && (
          <Box sx={{ pt: 1 }}>
            <FortnoxAccountPicker
              ownerId={ownerId}
              provider={fortnoxProvider}
              onSelect={(accountNumber) => setValue(name as any, accountNumber, { shouldDirty: true })}
            />
          </Box>
        )}
      </Stack>
    );
  };
  const fetchInvoiceGroupAccounting = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const result = await getInvoiceGroupAccountsById(id);
        if (!isMounted()) return;
        // console.log('fetchInvoiceGroupAccounting result', result);
        const account = result.data[0];
        setInvoiceGroupAccounts(account);
        setHasExistingAccount(!!account);
        reset({
          bankAccount: account?.bankAccount ?? '',
          memberAccount: account?.memberAccount ?? '',
          depositAccount: account?.depositAccount ?? '',
          fsPayServiceAccount: account?.fsPayServiceAccount ?? '',
          voucherSerie: account?.voucherSerie ?? '',
          eventAccount: account?.eventAccount ?? '',
          buyAccount: account?.buyAccount ?? '',
          clearingSwishAccount: account?.clearingSwishAccount ?? '',
          clearingStripeAccount: account?.clearingStripeAccount ?? '',
          excessAccount: account?.excessAccount ?? '',
        });
        await trigger();
      } catch (err) {
        if (isMounted()) ConsoleHelper.log(err);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, reset, trigger],
  );

  useEffect(() => {
    if (!params.id) return;
    fetchInvoiceGroupAccounting(+params.id);
  }, [params.id, fetchInvoiceGroupAccounting]);

  useEffect(() => {
    if (ownerId <= 0) return;
    getIntegrationsByOwnerId(ownerId)
      .then((res) => {
        const active = res.data.find((i) => i.isActive === 1);
        const provider = active?.provider ?? null;
        setFortnoxProvider(provider);
        if (provider && active) {
          getFortnoxAccounts(ownerId, provider)
            .then((accRes) => {
              setFortnoxAccountNumbers(new Set(accRes.data.map((a) => a.number)));
            })
            .catch(() => setFortnoxAccountNumbers(null));
        }
      })
      .catch(() => setFortnoxProvider(null));
  }, [ownerId]);

  const onSubmit = async (data: InvoiceGroupAccountInterface) => {
    setLoading(true);
    const dataToSubmit: InvoiceGroupAccount = {
      ...data,
      ownerConfigAccount_id: invoiceGroupAccounts?.ownerConfigAccount_id ?? 0,
      owner_id: invoiceGroupAccounts?.owner_id ?? params.id ? Number(params.id) : 0,
    };
    try {
      if (hasExistingAccount) {
        await updateInvoiceGroupAccounts(dataToSubmit);
      } else {
        // ConsoleHelper.log('Creating new invoice group account with data:', dataToSubmit);
        await createInvoiceGroupAccounts(dataToSubmit);
      }
      if (isMounted()) {
        toast.success(<>{t('invoice:adminInvoiceGroupAccountingTab.saveSuccess')}</>);
        fetchInvoiceGroupAccounting(Number(params.id));
      }
    } catch (err) {
      ConsoleHelper.log(err);
      toast.error(<>{t('invoice:adminInvoiceGroupAccountingTab.saveError')}</>);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {/* <LoadingOverlay loading={loading} /> */}
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {!loading && (
              <>
                <Grid container spacing={5}>
                  <Grid item xs={12} lg={7}>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {t('invoice:adminInvoiceGroupAccountingTab.bankAccountTitle')}
                        </Typography>
                        <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
                          {t('invoice:adminInvoiceGroupAccountingTab.bankAccountDescription')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderAccountField(t('invoice:adminInvoiceGroupAccountingTab.bankAccountLabel'), 'bankAccount')}
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {t('invoice:adminInvoiceGroupAccountingTab.revenueAccountTitle')}
                        </Typography>
                        <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
                          {t('invoice:adminInvoiceGroupAccountingTab.revenueAccountDescription')}
                        </Typography>
                        <Alert severity='info' sx={{ mt: 1 }}>
                          <Trans
                            i18nKey='invoice:adminInvoiceGroupAccountingTab.articleAccountFallback'
                            components={{
                              articleLink: <Link to={`${RoutesEnum.AdminInvoiceGroupSettings}/${ownerId}/articleaccounts`} />,
                            }}
                          />
                        </Alert>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderAccountField(t('invoice:adminInvoiceGroupAccountingTab.memberAccountLabel'), 'memberAccount')}
                      </Grid>{' '}
                      <Grid item xs={12} md={6}>
                        {renderAccountField(t('invoice:adminInvoiceGroupAccountingTab.despositLabel'), 'depositAccount')}
                      </Grid>{' '}
                      <Grid item xs={12} md={6}>
                        {renderAccountField(t('invoice:adminInvoiceGroupAccountingTab.eventAccountLabel'), 'eventAccount')}
                      </Grid>{' '}
                      <Grid item xs={12} md={6}>
                        {renderAccountField(t('invoice:adminInvoiceGroupAccountingTab.buyAccountLabel'), 'buyAccount')}
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {t('invoice:adminInvoiceGroupAccountingTab.paymentMethodTitle')}
                        </Typography>
                        <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
                          {t('invoice:adminInvoiceGroupAccountingTab.paymentMethodDescription')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderAccountField(t('invoice:adminInvoiceGroupAccountingTab.swishAccountLabel'), 'clearingSwishAccount')}
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {t('invoice:adminInvoiceGroupAccountingTab.receivableTitle')}
                        </Typography>
                        <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
                          {t('invoice:adminInvoiceGroupAccountingTab.receivableDescription')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderAccountField(t('invoice:adminInvoiceGroupAccountingTab.clearingStripeAccountLabel'), 'clearingStripeAccount')}
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {t('invoice:adminInvoiceGroupAccountingTab.excessTitle')}
                        </Typography>
                        <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
                          {t('invoice:adminInvoiceGroupAccountingTab.excessDescription')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {renderAccountField(t('invoice:adminInvoiceGroupAccountingTab.excessAccountLabel'), 'excessAccount')}
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mb={4}>
                      <Grid item xs={12}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {t('invoice:adminInvoiceGroupAccountingTab.voucherSerieTitle')}
                        </Typography>
                        <Typography variant='body2' color={(theme) => theme.palette.text.secondary}>
                          {t('invoice:adminInvoiceGroupAccountingTab.voucherSerieDescription')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ControlledTextField label={t('invoice:adminInvoiceGroupAccountingTab.voucherSerieLabel')} name='voucherSerie' />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} lg={5}>
                    <AdminInvoiceGroupAccountingIntegrationPanel />
                  </Grid>
                </Grid>
                <Divider />
                <Grid display='flex' justifyContent='flex-end' mt={2}>
                  <Button type='submit' variant='contained' disableElevation disabled={!isDirty || !isValid || loading}>
                    {t('common:button.save')}
                  </Button>
                </Grid>
              </>
            )}
          </form>
        </FormProvider>
      </Box>
    </>
  );
};
